import { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { Input } from "@/shared/ui/input";
import { Button } from "@/shared/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/shared/ui/card";
import { Badge } from "@/shared/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/ui/select";
import { toast } from "@/shared/widgets/toast";
import { UserEditDialog } from "./UserEditDialog";
import { UsersTableImproved } from "./UsersTableImproved";
import { UsersTableView } from "./UsersTableView";
import { useCustomQuery } from "@/shared/hooks/useCustomQuery";
import ProfilesService from "@/features/profiles/services/profiles/profileService";
import { IUser } from "@/features/profiles/services/profiles/types";
import { useCustomMutation } from "@/shared/hooks/useCustomMutation";
import { useDebounce } from "@/shared/hooks/useDebounce";
import { PageContent } from "@/shared/widgets/layout/PageContent";
import { Toggle } from "@/shared/ui/toggle";
import {
  Check,
  Search,
  Filter,
  Crown,
  Mail,
  User,
  LayoutGrid,
  Table2,
  Download,
  CalendarClock,
  Loader2,
} from "lucide-react";
import { cn } from "@/shared/lib/utils";
import { useTranslation } from "@/shared/i18n";
import { exportUsersToCsv } from "../utils/exportUsersToCsv";

type ViewMode = "cards" | "table";

const PERIOD_OPTIONS = [7, 15, 30, 60, 90];

export default function Users() {
  const { t } = useTranslation("profiles");
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(searchParams.get("search") ?? "");
  const [selectedUser, setSelectedUser] = useState<IUser | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const queryClient = useQueryClient();

  const [page, setPage] = useState(Number(searchParams.get("page") ?? "1") || 1);
  const [vipFilter, setVipFilter] = useState<boolean | undefined>(
    searchParams.get("vip") === "true" ? true : searchParams.get("vip") === "false" ? false : undefined,
  );
  const [subscribedFilter, setSubscribedFilter] = useState<boolean | undefined>(
    searchParams.get("subscriber") === "true"
      ? true
      : searchParams.get("subscriber") === "false"
        ? false
        : undefined,
  );
  const [isCreatorFilter, setIsCreatorFilter] = useState<boolean | undefined>(
    searchParams.get("isCreator") === "true"
      ? true
      : searchParams.get("isCreator") === "false"
        ? false
        : undefined,
  );
  const [pageSize, setPageSize] = useState(Number(searchParams.get("pageSize") ?? "10") || 10);
  const [viewMode, setViewMode] = useState<ViewMode>(
    (searchParams.get("view") as ViewMode) === "table" ? "table" : "cards",
  );
  const [newUsersDays, setNewUsersDays] = useState<number | undefined>(() => {
    const v = Number(searchParams.get("newUsersDays") ?? "");
    return PERIOD_OPTIONS.includes(v) ? v : undefined;
  });
  const [isExporting, setIsExporting] = useState(false);

  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  const createdAfter = useMemo(() => {
    if (!newUsersDays) return undefined;
    return new Date(Date.now() - newUsersDays * 24 * 60 * 60 * 1000).toISOString();
  }, [newUsersDays]);

  const queryParams = useMemo(
    () => ({
      search: debouncedSearchQuery,
      page,
      vip: vipFilter,
      subscriber: subscribedFilter,
      isCreator: isCreatorFilter,
      limit: pageSize,
      createdAfter,
    }),
    [debouncedSearchQuery, page, vipFilter, subscribedFilter, isCreatorFilter, pageSize, createdAfter],
  );

  const { data: paginatedUsers, isLoading } = useCustomQuery({
    serviceInstance: ProfilesService.Instance,
    selectAction: (s) => s.getProfiles,
    parameter: queryParams,
  });

  const { mutate: toggleVip } = useCustomMutation({
    serviceInstance: ProfilesService.Instance,
    selectAction: (s) => s.toggleVip,
    onSuccess: () => {
      toast.success(t("vipUpdated"));
      queryClient.invalidateQueries({ queryKey: ["get-profiles"] });
    },
    onError: (error: any) => {
      toast.error(t("errorUpdatingVip"), error?.message || t("errorUnexpected"));
    },
  });

  const handleVipToggle = ({ id, vip, vipExpiresAt }: { id: string; vip: boolean; vipExpiresAt: string }) => {
    toggleVip({ id, vip, vipExpiresAt });
  };

  // URL sync
  useEffect(() => {
    const params = new URLSearchParams();
    if (debouncedSearchQuery) params.set("search", debouncedSearchQuery);
    if (vipFilter !== undefined) params.set("vip", String(vipFilter));
    if (subscribedFilter !== undefined) params.set("subscriber", String(subscribedFilter));
    if (isCreatorFilter !== undefined) params.set("isCreator", String(isCreatorFilter));
    if (newUsersDays) params.set("newUsersDays", String(newUsersDays));
    if (page > 1) params.set("page", String(page));
    if (pageSize !== 10) params.set("pageSize", String(pageSize));
    if (viewMode !== "cards") params.set("view", viewMode);
    setSearchParams(params, { replace: true });
  }, [
    debouncedSearchQuery,
    vipFilter,
    subscribedFilter,
    isCreatorFilter,
    newUsersDays,
    page,
    pageSize,
    viewMode,
    setSearchParams,
  ]);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [debouncedSearchQuery, vipFilter, subscribedFilter, isCreatorFilter, newUsersDays]);

  const handlePageChange = (newPage: number) => setPage(newPage);

  const handleExport = async () => {
    if (isExporting) return;
    setIsExporting(true);
    try {
      await exportUsersToCsv({
        search: debouncedSearchQuery || undefined,
        vip: vipFilter,
        subscriber: subscribedFilter,
        isCreator: isCreatorFilter,
        createdAfter,
      });
      toast.success(t("exportSuccess"));
    } catch (e: any) {
      toast.error(t("exportError"), e?.message || t("errorUnexpected"));
    } finally {
      setIsExporting(false);
    }
  };

  const renderFilterToggle = (
    label: string,
    value: boolean | undefined,
    onChange: (value: boolean | undefined) => void,
    icon: React.ReactNode,
  ) => (
    <Toggle
      variant="outline"
      aria-label={t("filterBy", { label })}
      pressed={value !== undefined}
      onPressedChange={(pressed) => onChange(pressed ? true : undefined)}
      className={cn(
        "data-[state=on]:bg-primary data-[state=on]:text-primary-foreground transition-all duration-200",
        value !== undefined
          ? "bg-primary text-primary-foreground"
          : "bg-[#222222] border-white/10 text-white hover:bg-[#333333]",
      )}
    >
      {value !== undefined && <Check className="mr-2 h-4 w-4" />}
      {icon}
      <span className="ml-2">{label}</span>
    </Toggle>
  );

  const clearFilters = () => {
    setVipFilter(undefined);
    setSubscribedFilter(undefined);
    setIsCreatorFilter(undefined);
    setNewUsersDays(undefined);
    setSearchQuery("");
    setPage(1);
    setSearchParams({}, { replace: true });
  };

  const hasActiveFilters =
    vipFilter !== undefined ||
    subscribedFilter !== undefined ||
    isCreatorFilter !== undefined ||
    newUsersDays !== undefined ||
    Boolean(searchQuery);

  const periodLabel = (days: number) => {
    switch (days) {
      case 7:
        return t("last7Days");
      case 15:
        return t("last15Days");
      case 30:
        return t("last30Days");
      case 60:
        return t("last60Days");
      case 90:
        return t("last90Days");
      default:
        return `${days}d`;
    }
  };

  return (
    <PageContent title={t("users")}>
      <div className="min-h-screen bg-[#1A1A1A] space-y-6">
        {/* Filtros e Busca */}
        <Card className="bg-[#222222] border-white/10">
          <CardHeader>
            <CardTitle className="text-white flex items-center">
              <Filter className="w-5 h-5 mr-2" />
              {t("filtersAndSearch")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                type="text"
                placeholder={t("searchPlaceholder")}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="bg-[#1E1E1E] border-[#333333] text-white placeholder:text-gray-400 pl-10"
              />
            </div>

            <div className="flex flex-wrap gap-3 items-center">
              {renderFilterToggle(t("vip"), vipFilter, setVipFilter, <Crown className="w-4 h-4" />)}
              {renderFilterToggle(
                t("subscriber"),
                subscribedFilter,
                setSubscribedFilter,
                <Mail className="w-4 h-4" />,
              )}
              {renderFilterToggle(
                t("creator"),
                isCreatorFilter,
                setIsCreatorFilter,
                <User className="w-4 h-4" />,
              )}

              <div className="flex items-center gap-2">
                <CalendarClock className="w-4 h-4 text-white/60" />
                <Select
                  value={newUsersDays ? String(newUsersDays) : "all"}
                  onValueChange={(v) => setNewUsersDays(v === "all" ? undefined : Number(v))}
                >
                  <SelectTrigger className="w-[200px] bg-[#1E1E1E] border-[#333333] text-white">
                    <SelectValue placeholder={t("registrationPeriod")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t("allTime")}</SelectItem>
                    {PERIOD_OPTIONS.map((d) => (
                      <SelectItem key={d} value={String(d)}>
                        {periodLabel(d)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {hasActiveFilters && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={clearFilters}
                  className="bg-red-500/20 border-red-500/30 text-red-400 hover:bg-red-500/30"
                >
                  {t("clearFilters")}
                </Button>
              )}
            </div>

            {hasActiveFilters && (
              <div className="flex flex-wrap gap-2">
                {searchQuery && (
                  <Badge variant="secondary" className="bg-blue-500/20 text-blue-400 border-blue-500/30">
                    {t("search")}: "{searchQuery}"
                  </Badge>
                )}
                {vipFilter !== undefined && (
                  <Badge
                    variant="secondary"
                    className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30"
                  >
                    {t("vip")}: {vipFilter ? t("yes") : t("no")}
                  </Badge>
                )}
                {subscribedFilter !== undefined && (
                  <Badge variant="secondary" className="bg-green-500/20 text-green-400 border-green-500/30">
                    {t("subscriber")}: {subscribedFilter ? t("yes") : t("no")}
                  </Badge>
                )}
                {isCreatorFilter !== undefined && (
                  <Badge variant="secondary" className="bg-purple-500/20 text-purple-400 border-purple-500/30">
                    {t("creator")}: {isCreatorFilter ? t("yes") : t("no")}
                  </Badge>
                )}
                {newUsersDays !== undefined && (
                  <Badge variant="secondary" className="bg-cyan-500/20 text-cyan-400 border-cyan-500/30">
                    {t("registrationPeriod")}: {periodLabel(newUsersDays)}
                  </Badge>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Lista de Usuários */}
        <div>
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <h2 className="text-lg font-semibold text-white">{t("users")}</h2>

            <div className="flex items-center gap-2">
              <div className="flex items-center rounded-md border border-white/10 bg-[#222222] p-1">
                <Button
                  variant={viewMode === "cards" ? "default" : "ghost"}
                  size="sm"
                  className={cn(
                    "h-8 gap-1.5 px-2",
                    viewMode === "cards"
                      ? "bg-primary text-primary-foreground"
                      : "text-white/60 hover:text-white hover:bg-white/5",
                  )}
                  onClick={() => setViewMode("cards")}
                  aria-label={t("viewCards")}
                >
                  <LayoutGrid className="h-4 w-4" />
                  <span className="hidden sm:inline">{t("viewCards")}</span>
                </Button>
                <Button
                  variant={viewMode === "table" ? "default" : "ghost"}
                  size="sm"
                  className={cn(
                    "h-8 gap-1.5 px-2",
                    viewMode === "table"
                      ? "bg-primary text-primary-foreground"
                      : "text-white/60 hover:text-white hover:bg-white/5",
                  )}
                  onClick={() => setViewMode("table")}
                  aria-label={t("viewTable")}
                >
                  <Table2 className="h-4 w-4" />
                  <span className="hidden sm:inline">{t("viewTable")}</span>
                </Button>
              </div>

              <Button
                variant="outline"
                size="sm"
                onClick={handleExport}
                disabled={isExporting || !paginatedUsers || paginatedUsers.totalItems === 0}
                className="h-9 gap-2 bg-[#1E1E1E] border-[#333333] text-white hover:bg-[#2A2A2A]"
              >
                {isExporting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Download className="h-4 w-4" />
                )}
                {isExporting ? t("exporting") : t("exportCsv")}
              </Button>
            </div>
          </div>

          {viewMode === "cards" ? (
            <UsersTableImproved
              users={paginatedUsers?.data || []}
              isLoading={isLoading}
              onVipToggle={handleVipToggle}
              pagination={
                paginatedUsers
                  ? {
                      currentPage: page,
                      totalPages: paginatedUsers.totalPage,
                      totalItems: paginatedUsers.totalItems,
                      pageSize,
                      onPageChange: handlePageChange,
                      onPageSizeChange: (size) => {
                        setPageSize(size);
                        setPage(1);
                      },
                      pageSizeOptions: [10, 20, 50, 100],
                      showPageSizeOptions: true,
                      showTotalItems: true,
                      syncWithQueryParams: false,
                    }
                  : undefined
              }
            />
          ) : (
            <UsersTableView
              users={paginatedUsers?.data || []}
              isLoading={isLoading}
              onVipToggle={handleVipToggle}
              pagination={
                paginatedUsers
                  ? {
                      currentPage: page,
                      totalPages: paginatedUsers.totalPage,
                      totalItems: paginatedUsers.totalItems,
                      pageSize,
                      onPageChange: handlePageChange,
                      onPageSizeChange: (size) => {
                        setPageSize(size);
                        setPage(1);
                      },
                      pageSizeOptions: [10, 20, 50, 100],
                      showPageSizeOptions: true,
                      showTotalItems: true,
                      syncWithQueryParams: false,
                    }
                  : undefined
              }
            />
          )}
        </div>
      </div>

      <UserEditDialog user={selectedUser} open={editDialogOpen} onOpenChange={setEditDialogOpen} />
    </PageContent>
  );
}
